import {
  BOGGLE_RUN_SUMMARY_RATINGS,
  type BoggleRunSummaryRatingId,
} from '~/boggle/config/runSummaryScore'
import type { BoggleBadgeId } from '~/boggle/config/badges/types'
import { collapseEarnedBadgesForDisplay } from '~/boggle/lib/badges/collapseEarnedBadges'
import { badgeIdsIncludingLowerTiers } from './badgeProjections'
import type {
  BoggleBadgeProgressProjection,
  BoggleBadgeProgressRecord,
  BoggleCompletedRun,
  BoggleUnviewedBadge,
  BoggleUnviewedBadgeKind,
  BoggleUnviewedWord,
  BoggleWordStat,
} from './types'
import { normalizeBoggleSubmittedWord } from './wordMetadata'

const RATING_RANK = new Map<BoggleRunSummaryRatingId, number>(
  BOGGLE_RUN_SUMMARY_RATINGS.map((rating, index) => [rating.id, index]),
)

export function isBoggleRunSummaryRatingHigherThan(
  candidate: BoggleRunSummaryRatingId,
  baseline: BoggleRunSummaryRatingId | null,
) {
  if (baseline === null) {
    return false
  }
  return (RATING_RANK.get(candidate) ?? -1) > (RATING_RANK.get(baseline) ?? -1)
}

export function hasEarnedBoggleBadgeRecord(
  record: BoggleBadgeProgressRecord | undefined,
) {
  if (!record) {
    return false
  }
  if (record.highestRating !== null) {
    return true
  }
  return Object.values(record.countsByRating).some(count => (count ?? 0) > 0)
}

export function getBoggleCompletedRunDisplayWords(
  run: BoggleCompletedRun,
): readonly string[] {
  const acceptedWords = run.acceptedWordTimeline?.map(word => word.word)
  if (acceptedWords && acceptedWords.length > 0) {
    return acceptedWords
  }
  return run.submittedWords
}

export function computeBoggleUnviewedBadgesFromRun({
  earnedBadges,
  priorBadgeProgress,
  runRatingId,
}: {
  earnedBadges: BoggleCompletedRun['earnedBadges']
  priorBadgeProgress: BoggleBadgeProgressProjection
  runRatingId: BoggleRunSummaryRatingId
}): BoggleUnviewedBadge[] {
  const results: BoggleUnviewedBadge[] = []
  const collapsed = collapseEarnedBadgesForDisplay(earnedBadges ?? [])

  for (const earned of collapsed) {
    for (const badgeId of badgeIdsIncludingLowerTiers(earned.badgeId)) {
      const prior = priorBadgeProgress.perBadge[badgeId]
      let kind: BoggleUnviewedBadgeKind | null = null

      if (!hasEarnedBoggleBadgeRecord(prior)) {
        kind = 'new'
      } else if (isBoggleRunSummaryRatingHigherThan(runRatingId, prior?.highestRating ?? null)) {
        kind = 'improved'
      }

      if (kind === null) {
        continue
      }

      results.push({
        badgeId,
        kind,
        ratingId: runRatingId,
      })
    }
  }

  return results
}

export function computeBoggleUnviewedWordsFromRun({
  displayWords,
  priorWordStats,
}: {
  displayWords: readonly string[]
  priorWordStats: readonly BoggleWordStat[]
}): BoggleUnviewedWord[] {
  const knownWords = new Set(priorWordStats.map(stat => stat.normalizedWord))
  const results: BoggleUnviewedWord[] = []
  const seenThisRun = new Set<string>()

  for (const displayWord of displayWords) {
    const normalizedWord = normalizeBoggleSubmittedWord(displayWord)
    if (!normalizedWord || knownWords.has(normalizedWord) || seenThisRun.has(normalizedWord)) {
      continue
    }
    seenThisRun.add(normalizedWord)
    results.push({ normalizedWord })
  }

  return results
}

export function isBoggleUnviewedBadgeVisibleForFilter({
  countsByRating,
  filter,
  unviewed,
}: {
  countsByRating: Partial<Record<BoggleRunSummaryRatingId, number>>
  filter: 'all' | BoggleRunSummaryRatingId
  unviewed: BoggleUnviewedBadge
}) {
  if (filter === 'all') {
    return true
  }
  return (countsByRating[filter] ?? 0) > 0
}

export function mergeBoggleUnviewedBadges(
  existing: readonly BoggleUnviewedBadge[],
  incoming: readonly BoggleUnviewedBadge[],
): BoggleUnviewedBadge[] {
  const merged = new Map<BoggleBadgeId, BoggleUnviewedBadge>(
    existing.map(entry => [entry.badgeId, entry]),
  )

  for (const entry of incoming) {
    const previous = merged.get(entry.badgeId)
    if (!previous) {
      merged.set(entry.badgeId, entry)
      continue
    }
    if (previous.kind === 'new') {
      continue
    }
    if (entry.kind === 'new') {
      merged.set(entry.badgeId, entry)
      continue
    }
    if (
      isBoggleRunSummaryRatingHigherThan(entry.ratingId, previous.ratingId) ||
      entry.ratingId === previous.ratingId
    ) {
      merged.set(entry.badgeId, entry)
    }
  }

  return [...merged.values()]
}
